import React, { useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { Box, Card, Checkbox, FormControlLabel } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { MRT_ColumnDef, MRT_PaginationState, MRT_Row, MRT_RowData } from 'material-react-table';

import { usePersistentState } from 'common/hooks';

import { ClaimLicense } from 'commonComponents/ClaimLicense';
import { LinkButton } from 'commonComponents/LinkButton';
import { PageContainer } from 'commonComponents/PageContainer';
import { SelectProduct } from 'commonComponents/SelectProduct';

import { useUser } from 'api/authorization';
import { type ProductConfiguration, useProductConfigurations } from 'api/productConfigurations';
import { useCurrentProduct } from 'api/products';
import { CreateIcon } from 'icons/CreateIcon';
import { ROLE_DEFINITIONS } from 'roles/definitions';
import { RoleDependentComponent } from 'roles/RoleDependentComponent';
import { ROUTES } from 'routes/routes';

import { MaterialTable } from '../commonComponents/MaterialTable';
import { approxDaysForDuration, durationOrUnlimited } from './durationUnits';

function compareValidities(rowA: MRT_RowData, rowB: MRT_RowData) {
    return (
        approxDaysForDuration(rowA.original.validity, rowA.original.validityUnit) -
        approxDaysForDuration(rowB.original.validity, rowB.original.validityUnit)
    );
}

function compareSupportValidities(rowA: MRT_RowData, rowB: MRT_RowData) {
    return (
        approxDaysForDuration(rowA.original.supportValidity, rowA.original.supportValidityUnit) -
        approxDaysForDuration(rowB.original.supportValidity, rowB.original.supportValidityUnit)
    );
}

let columns: Array<MRT_ColumnDef<ProductConfiguration>> = [
    { header: 'Id', accessorKey: 'id' },
    {
        header: 'Product Name',
        accessorFn: configuration => (configuration.product ? configuration.product.name : 'N/A'),
    },
    { header: 'Configuration Name', accessorKey: 'name' },
    {
        header: 'Application Names',
        accessorFn: configuration => configuration.runtimeFeatures?.map(f => f.name).join(', ') || '',
    },
    {
        header: 'Validity',
        accessorFn: configuration => durationOrUnlimited(configuration.validity, configuration.validityUnit),
        sortingFn: (rowA: MRT_RowData, rowB: MRT_RowData) => compareValidities(rowA, rowB),
    },
    {
        header: 'Support Period',
        accessorFn: configuration =>
            durationOrUnlimited(configuration.supportValidity, configuration.supportValidityUnit),
        sortingFn: (rowA: MRT_RowData, rowB: MRT_RowData) => compareSupportValidities(rowA, rowB),
    },
    { header: 'Part No.', accessorKey: 'partNo' },
];
columns = columns.map(c => ({ ...c, id: c.header }));

export function ConfigurationList() {
    const navigate = useNavigate();
    const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 100 });
    const { data: currentProduct } = useCurrentProduct();
    const { data: user } = useUser();
    const userHasProduct = Boolean(user?.products?.length);
    const productIsSelected = Boolean(currentProduct);
    const definedColumnVisibility = [
        { id: 'Application Names', isVisible: currentProduct?.is_application },
        { id: 'Id', isVisible: false },
    ];

    const defaultAdditionalFilters = useMemo(
        () => ({
            showInvalidatedConfigurations: false,
            product: currentProduct?.id,
        }),
        [currentProduct]
    );
    const [additionalFilters, persistAdditionalFilters] = usePersistentState(
        'productConfigurationAdditionalFilters',
        defaultAdditionalFilters
    );
    useEffect(() => {
        if (currentProduct && additionalFilters.product !== currentProduct.id) {
            persistAdditionalFilters(defaultAdditionalFilters);
        }
    }, [additionalFilters, currentProduct, defaultAdditionalFilters, persistAdditionalFilters]);

    const { data, isInitialLoading } = useProductConfigurations(
        {
            product: currentProduct?.id,
            invalidated: additionalFilters.showInvalidatedConfigurations ? undefined : false,
        },
        Boolean(currentProduct)
    );
    const productConfigurations = useMemo(() => data || [], [data]);

    const defaultSort = [{ id: 'Id', desc: true }];
    const showDetails = (row: MRT_Row<ProductConfiguration>) =>
        navigate(generatePath(ROUTES.CONFIGURATION_DETAIL, { id: row.original.id.toString() }));

    const Buttons = (
        <ConfigurationListFilters
            showInvalidatedConfigurations={additionalFilters.showInvalidatedConfigurations}
            setShowInvalidatedConfigurations={(flag: boolean) =>
                persistAdditionalFilters({ ...additionalFilters, showInvalidatedConfigurations: flag })
            }
        />
    );

    return (
        <PageContainer dataTestId="ConfigurationList_Container" maxWidth={false} maxHeight="100%">
            {!userHasProduct && <ClaimLicense />}
            {userHasProduct && !productIsSelected && <SelectProduct />}
            {userHasProduct && productIsSelected && (
                <>
                    <Card sx={{ padding: 1, marginBottom: 1, overflow: 'inherit' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <ConfigurationListActions />
                        </Box>
                    </Card>
                    <MaterialTable
                        name="productConfigurations"
                        data={productConfigurations || []}
                        columns={columns}
                        defaultSort={defaultSort}
                        onRowClicked={showDetails}
                        pagination={pagination}
                        setPagination={setPagination}
                        isInitialLoading={isInitialLoading}
                        definedColumnVisibility={definedColumnVisibility}
                        customActions={Buttons}
                    />
                </>
            )}
        </PageContainer>
    );
}

function ConfigurationListFilters({ showInvalidatedConfigurations, setShowInvalidatedConfigurations }) {
    return (
        <Box
            sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'row',
                paddingLeft: 5,
            }}
        >
            <FormControlLabel
                control={
                    <Checkbox
                        checked={showInvalidatedConfigurations}
                        onChange={event => setShowInvalidatedConfigurations(event.target.checked)}
                        name="showInvalidatedConfigurations"
                    />
                }
                label="Show Invalid Product Configurations"
            />
        </Box>
    );
}

function ConfigurationListActions() {
    const theme = useTheme();

    return (
        <Box
            sx={{
                display: 'flex',
                gap: theme.spacing(2),
            }}
        >
            <RoleDependentComponent
                userRole={ROLE_DEFINITIONS.LicenseManager}
                component={LinkButton}
                iconClass={CreateIcon}
                route={ROUTES.CONFIGURATION_CREATE}
                title="Create"
            />
        </Box>
    );
}
